import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, TextField } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import DataTablePage from 'components/DataTablePage';
import gameLeaderboardsColumnData from 'data/gameLeaderboardsColumnData';
import { getGames } from 'services/games';
import { createLeaderboard, getBetLimitsForGame, getLeaderboards } from 'services/game-leaderboards';
import { Can } from 'context';

const initialForm = {
  name: '',
  description: '',
  gameId: '',
  betLimitId: '',
  startAt: '',
  endAt: ''
};

function Leaderboards() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [games, setGames] = useState([]);
  const [betLimits, setBetLimits] = useState([]);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getGames(200, 1).then((res) => setGames(res?.data || []));
  }, []);

  useEffect(() => {
    if (!form.gameId) {
      setBetLimits([]);
      return;
    }
    getBetLimitsForGame(form.gameId).then((res) => setBetLimits(res || []));
  }, [form.gameId]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.name || !form.gameId || !form.startAt || !form.endAt) {
      setError('Name, game, start date and end date are required.');
      return;
    }

    const startAtIso = new Date(form.startAt).toISOString();
    const endAtIso = new Date(form.endAt).toISOString();
    if (new Date(startAtIso) >= new Date(endAtIso)) {
      setError('Start date must be before end date.');
      return;
    }

    const payload = {
      name: form.name,
      description: form.description || null,
      gameId: Number(form.gameId),
      betLimitId: form.betLimitId || null,
      startAt: startAtIso,
      endAt: endAtIso
    };

    try {
      await createLeaderboard(payload);
      setDialogOpen(false);
      setForm(initialForm);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not create leaderboard';
      setError(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  const createButton = (
    <Can I='manage' a='leaderboard'>
      <MDButton variant='contained' color='info' onClick={() => setDialogOpen(true)}>
        Create leaderboard
      </MDButton>
    </Can>
  );

  const fetchLeaderboards = (limit, page) => getLeaderboards(limit, page);
  return (
    <>
      <DataTablePage
        title='Leaderboards'
        createButton={createButton}
        canSearch
        fetchData={fetchLeaderboards}
        queryKey='leaderboards'
        columnData={gameLeaderboardsColumnData}
        object={'leaderboard'}
        noActions
        filters={{ refreshKey }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Create leaderboard</DialogTitle>
        <DialogContent dividers>
          <MDBox display='flex' flexDirection='column' gap={2} mt={1}>
            <MDInput label='Name' fullWidth value={form.name} onChange={handleInputChange('name')} />
            <MDInput
              label='Description'
              fullWidth
              value={form.description}
              onChange={handleInputChange('description')}
            />
            <TextField
              select
              label='Game'
              value={form.gameId}
              onChange={handleInputChange('gameId')}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  height: '44px', // match MDInput
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {games.map(
                (game) =>
                  game.client && (
                    <MenuItem key={game.id} value={game.id}>
                      {game.name}({game.id}) {game.client.name}
                    </MenuItem>
                  )
              )}
            </TextField>

            <TextField
              select
              label='Bet limit (optional)'
              value={form.betLimitId}
              onChange={handleInputChange('betLimitId')}
              fullWidth
              disabled={!betLimits.length}
              sx={{
                '& .MuiInputBase-root': {
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value=''>Any</MenuItem>
              {betLimits.map((betLimit) => (
                <MenuItem key={betLimit.id} value={betLimit.id}>
                  {betLimit.id}{' '}
                  {betLimit.singleBet
                    ? `${betLimit.singleBet}$`
                    : `${betLimit.minTotalBet}$ - ${betLimit.maxTotalBet}$ total`}
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDInput
                  label='Start (UTC)'
                  type='datetime-local'
                  fullWidth
                  value={form.startAt}
                  onChange={handleInputChange('startAt')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  label='End (UTC)'
                  type='datetime-local'
                  fullWidth
                  value={form.endAt}
                  onChange={handleInputChange('endAt')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            {error && (
              <MDBox color='error.main' fontSize='0.875rem'>
                {error}
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='text' color='secondary' onClick={() => setDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant='gradient' color='info' onClick={handleSubmit}>
            Create
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Leaderboards;
